import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { fetchData } from '../../../global/utils/fetch'
import {
  createChatSession,
  sendChatMessage,
} from '../../api'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from './styles.module.css'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

function ChatPanel() {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [sessionObjectId, setSessionObjectId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const initSession = useCallback(async () => {
    try {
      const data = await fetchData<{ objectId: string }>(createChatSession())
      setSessionObjectId(data.objectId)
      setMessages([])
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to create chat session.',
        severity: 'error',
      })
    }
  }, [])

  useEffect(() => {
    initSession()
  }, [initSession])

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [messages, isLoading])

  async function handleSend() {
    const content = inputValue.trim()
    if (!content || !sessionObjectId || isLoading) return

    setInputValue('')
    setMessages((prev) => [...prev, { role: 'user', content }])
    setIsLoading(true)

    try {
      const data = await fetchData<{
        message: { content: string }
        pipelineResult: { answer: string; sources: Array<{ documentName: string }> }
      }>(sendChatMessage({ sessionObjectId, content }))

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.pipelineResult.answer },
      ])
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Error: ${error.message || 'Failed to get response.'}`,
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  async function handleNewSession() {
    await initSession()
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>Chat</h3>
        <div className={styles.headerActions}>
          <SecondaryButton
            buttonName='New Session'
            onClick={handleNewSession}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className={styles.messagesArea}>
        {messages.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            Ask the Technician Assistant a question.
          </div>
        )}
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={
              msg.role === 'user'
                ? styles.userMessage
                : styles.assistantMessage
            }
          >
            {msg.content}
          </div>
        ))}
        {isLoading && <div className={styles.thinking}>Thinking...</div>}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputArea}>
        <textarea
          className={styles.input}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Ask a question...'
          rows={1}
          disabled={isLoading || !sessionObjectId}
        />
        <button
          className={styles.sendButton}
          onClick={handleSend}
          disabled={isLoading || !inputValue.trim() || !sessionObjectId}
        >
          Send
        </button>
      </div>
    </div>
  )
}

export default ChatPanel
